import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";

import { buildDayRows } from "../../../../utils/nutrition-progress";
import { useNutritionProgressFilters } from "./useNutritionProgressFilters";
import { useNutritionProgressData } from "./useNutritionProgressData";
import { useExtendedHistoryData } from "./useExtendedHistoryData";
import PlanStatusList from "./PlanStatusList";
import RangeControls from "./RangeControls";
import ViewSwitch from "./ViewSwitch";
import TableTemplateControls from "./TableTemplateControls";
import DayDrawer from "./views/DayDrawer";
import { DEFAULT_PROGRESS_VIEW, viewComponentFor } from "./views";

// New Nutrition Progress screen. Phase 3 wires the LOGIC for the three
// persistent headers (derived flags · range controls · plan status bar) through
// the pure engine + useNutritionProgressFilters. The UI here is intentionally
// unstyled scaffolding — real design / i18n land in later phases.
//
// Kept intentionally thin — an orchestrator, not a component that does
// everything itself. Range controls (RangeControls.js), the derived per-day
// data (useNutritionProgressData.js) and the Customise Table dropdown/
// button/modal (TableTemplateControls.js) all live in their own files;
// this component just wires them together and owns the state genuinely
// shared between them (activeView, templates).

const toUtcNum = (date) => Number(moment(date).format("YYYYMMDD"));

const NutritionProgress = ({
  nutritionPlans = [],
  nutritionTableTemplates = [],
  currentNutritionPlan,
  dailyStats = [],
  activeExercises = [],
  dayPersonalRecords = {},
  habitLogs = [],
  femaleCycleLogs = [],
  femaleCycleSettings = null,
  athlete,
  activeUser,
  bodyweightGoal = null,
  tdeeOverride = null,
}) => {
  const todayUtc = toUtcNum(new Date());

  const [activeView, setActiveView] = useState(DEFAULT_PROGRESS_VIEW);
  // Day Drawer — triggered from a day click in Calendar/Log (see CalCell.js /
  // LogGrid.js). Owned here, not inside either view, so the same drawer
  // instance serves both without duplicating state.
  const [selectedDrawerDay, setSelectedDrawerDay] = useState(null);
  const planStatusFilter = null;
  const plannerOverride = null;
  // Lives here (not inside SummaryView.js, unlike the bodyweight-goal
  // override) because ReportsView.js's own "7-day rolling intake vs
  // bodyweight" chart computes its own buildEnergyExpenditureStats(dayRows)
  // independently of Summary's — if the override state lived inside
  // SummaryView.js, Reports would keep showing the un-adjusted TDEE after a
  // coach saves one from Summary. Resolving it once, here, and handing the
  // same value to both views keeps them mathematically unable to disagree.
  const [tdeeOverrideState, setTdeeOverrideState] = useState(tdeeOverride);

  // Shared with TableTemplateControls — the templates list AND which one is
  // active both need to live here, since both TableTemplateControls (the
  // dropdown) and ActiveView (TableView, to filter its columns) read them.
  const [templates, setTemplates] = useState(nutritionTableTemplates);
  const [activeTemplateId, setActiveTemplateId] = useState(null);
  const activeTemplate = templates.find((tpl) => String(tpl._id) === activeTemplateId) || null;
  // null = "Default View" (no template selected) → the table shows every
  // column; a Set of the template's saved keys otherwise. Memoized — a fresh
  // Set every render (this component re-renders for reasons unrelated to the
  // template, e.g. the Day Drawer opening) would defeat DayRow/AvgRow's own
  // memo below, since it flows straight through as their activeColumns prop.
  const activeColumns = useMemo(
    () => (activeTemplate ? new Set(activeTemplate.columns) : null),
    [activeTemplate]
  );

  const {
    selectedPlanId,
    dateFrom,
    dateTo,
    activeQuick,
    flags,
    selectPlan,
    applyQuickRange,
    changeDate,
  } = useNutritionProgressFilters({
    nutritionPlans,
    currentNutritionPlan,
    athlete,
    hasBodyweightGoal: bodyweightGoal != null,
  });

  // null = use real derived value from flags; true/false = manual preview override
  const activePlanner = plannerOverride ?? flags.hasPlanner;

  // Only the selected view is mounted, so the other four don't compute.
  const ActiveView = viewComponentFor(activeView);

  // Extends activeExercises/dayPersonalRecords/habitLogs past the SSR's
  // trailing-1-year window on demand — see useExtendedHistoryData's own
  // header comment for the full "why". Starts from the raw props (exactly
  // what nutrition.controller.js's loaders fetched); ensureRangeLoaded only
  // fires a client fetch once dateFrom actually reaches earlier than that.
  const {
    activeExercises: extendedActiveExercises,
    dayPersonalRecords: extendedDayPersonalRecords,
    habitLogs: extendedHabitLogs,
    isLoadingHistory,
    ensureRangeLoaded,
  } = useExtendedHistoryData({
    proxyAthlete: athlete?._id,
    initialActiveExercises: activeExercises,
    initialDayPersonalRecords: dayPersonalRecords,
    initialHabitLogs: habitLogs,
  });

  // dateFrom changes for any reason — quick-select ("lifetime"), a manual
  // date, or picking a plan — ensureRangeLoaded itself is the one that
  // decides whether that actually reaches earlier than what's loaded.
  useEffect(() => {
    if (dateFrom) ensureRangeLoaded(dateFrom, dateTo);
  }, [dateFrom, dateTo, ensureRangeLoaded]);

  const { dayRows, weekGridDayRows, prExerciseKeysByDay, repMaxPR } = useNutritionProgressData({
    dailyStats,
    nutritionPlans,
    activeExercises: extendedActiveExercises,
    dateFrom,
    dateTo,
    femaleCycleLogs,
    femaleCycleSettings,
    dayPersonalRecords: extendedDayPersonalRecords,
  });

  // TDEE window: always the trailing 3 months ending today, decoupled from
  // whatever reporting period (dateFrom/dateTo) is selected above — product
  // ask (sc-45730-ish): a coach reviewing a short range (e.g. "1 week") was
  // seeing "need more data" even when months of real history already exist,
  // because the TDEE calc was reading the same range-scoped dayRows as
  // everything else. Mirrors BodyweightTrendCard.js's own "always anchored
  // to today, independent of the nutrition period" pattern — deliberately a
  // FIXED window, not "the selected range, with a 3-month floor": letting it
  // grow with a longer selection (6 months/lifetime) would dilute the
  // estimate with stale, possibly-different-physiology data instead of
  // reflecting the client's current metabolic state. nutritionPlans/
  // activeExercises are empty — buildEnergyExpenditureStats only reads
  // consumed/weight/utcDate from each row, same as BodyweightTrendCard's own
  // buildDayRows call. Computed once here (not per-view) for the same reason
  // tdeeOverrideState lives here — so Summary and Reports can never disagree.
  const tdeeDayRows = useMemo(
    () =>
      buildDayRows({
        dailyStats,
        nutritionPlans: [],
        activeExercises: [],
        dateFrom: moment().subtract(3, "months").toDate(),
        dateTo: new Date(),
      }),
    [dailyStats]
  );

  return (
    <div className="Nutrition-Progress-New flex flex-col">
      {/* ---- Range controls (plan / from / to / quick) ------------------- */}
      <RangeControls
        nutritionPlans={nutritionPlans}
        selectedPlanId={selectedPlanId}
        onSelectPlan={selectPlan}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onChangeDate={changeDate}
        activeQuick={activeQuick}
        onApplyQuickRange={applyQuickRange}
      />

      {/* ---- Plans in range · one PlanStatusBar per plan overlapping the range */}
      <PlanStatusList
        nutritionPlans={nutritionPlans}
        dateFrom={dateFrom}
        dateTo={dateTo}
        planStatusFilter={planStatusFilter}
        todayUtc={todayUtc}
      />

      {/* ---- View switch · summary / calendar / log / table / graphs ------ */}
      <div className="np-view-switch-row">
        <ViewSwitch activeView={activeView} onChange={setActiveView} />
        <TableTemplateControls
          activeView={activeView}
          templates={templates}
          onTemplateSaved={(tpl) =>
            setTemplates((prev) => {
              // Update in place if this _id already exists (edit), else
              // prepend (create) — same callback handles both since
              // TableTemplateControls.js's handleSaveTemplate POSTs and
              // PUTs to the same client-side flow.
              const exists = prev.some((p) => String(p._id) === String(tpl._id));
              return exists
                ? prev.map((p) => (String(p._id) === String(tpl._id) ? tpl : p))
                : [tpl, ...prev];
            })
          }
          onTemplateDeleted={(id) =>
            setTemplates((prev) => prev.filter((tpl) => String(tpl._id) !== String(id)))
          }
          intensityMeasure={activeUser.intensityMeasure || "rpe"}
          activeTemplateId={activeTemplateId}
          onActiveTemplateChange={setActiveTemplateId}
        />
      </div>

      {/* ---- Active view content ----------------------------------------- */}
      {/* min-w-0 only for Calendar/Log/Table: each is the actual flex item
          under this flex-col wrapper, so it needs to be allowed to shrink
          below its content's intrinsic width — otherwise the grid/table's
          min-width pushes this wrapper past the viewport instead of
          scrolling inside its own overflow-x container, producing a second
          page-level scrollbar. Scoped by view id so Summary/Graphs (which
          don't have this problem) keep their default, unconstrained flex
          sizing. */}
      <div className={["calendar", "log", "table"].includes(activeView) ? "min-w-0" : ""}>
        <ActiveView
          dayRows={dayRows}
          weekGridDayRows={weekGridDayRows}
          tdeeDayRows={tdeeDayRows}
          dailyStats={dailyStats}
          activeExercises={extendedActiveExercises}
          prExerciseKeysByDay={prExerciseKeysByDay}
          repMaxPR={repMaxPR}
          habitLogs={extendedHabitLogs}
          isLoadingHistory={isLoadingHistory}
          dateFrom={dateFrom}
          dateTo={dateTo}
          units={activeUser.units || "metric"}
          intensityMeasure={activeUser.intensityMeasure || "rpe"}
          activeColumns={activeColumns}
          nutritionPlans={nutritionPlans}
          nutritionTableTemplates={templates}
          athlete={athlete}
          hasPlanner={activePlanner}
          bodyweightGoal={bodyweightGoal}
          tdeeOverride={tdeeOverrideState}
          onTdeeOverrideSaved={setTdeeOverrideState}
          onTdeeOverrideReset={() => setTdeeOverrideState(null)}
          onNavigateToReports={() => setActiveView("reports")}
          onDayClick={setSelectedDrawerDay}
        />
      </div>

      <DayDrawer
        day={selectedDrawerDay}
        onClose={() => setSelectedDrawerDay(null)}
        dayRows={dayRows}
        dailyStats={dailyStats}
        nutritionPlans={nutritionPlans}
        units={activeUser.units || "metric"}
      />
    </div>
  );
};


NutritionProgress.propTypes = {
  nutritionPlans: PropTypes.arrayOf(PropTypes.object),
  nutritionTableTemplates: PropTypes.arrayOf(PropTypes.object),
  currentNutritionPlan: PropTypes.object,
  dailyStats: PropTypes.arrayOf(PropTypes.object),
  activeExercises: PropTypes.arrayOf(PropTypes.object),
  habitLogs: PropTypes.arrayOf(PropTypes.object),
  femaleCycleLogs: PropTypes.arrayOf(PropTypes.object),
  femaleCycleSettings: PropTypes.object,
  athlete: PropTypes.object,
  activeUser: PropTypes.object,
  bodyweightGoal: PropTypes.object,
  tdeeOverride: PropTypes.object,
};

export default NutritionProgress;
